import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Hand, Bot, Send, Loader2, UserRound } from "lucide-react";
import { takeoverConversation, releaseConversation, sendManualMessage } from "@/lib/dentiumApi";

const errText = (e, fb) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : fb);

const useConvMutation = (fn, ok) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["conversations"] }); ok?.(d); },
    onError: (e) => toast.error(errText(e, "No se pudo completar la acción")),
  });
};

export const ManualBanner = ({ conv }) => {
  const release = useConvMutation(() => releaseConversation(conv.id), () => toast.success("Conversación devuelta al bot"));
  return (
    <div data-testid="manual-mode-banner" className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-200">
      <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
        <UserRound className="h-4 w-4" />
        <span>Modo manual activo{conv.manual_by ? ` · gestionado por ${conv.manual_by}` : ""}</span>
      </div>
      <button
        data-testid="release-to-bot-btn"
        onClick={() => release.mutate()}
        disabled={release.isPending}
        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 px-3.5 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50"
      >
        {release.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />} Devolver al bot
      </button>
    </div>
  );
};

export const ManualControl = ({ conv, accent }) => {
  const [text, setText] = useState("");
  const takeover = useConvMutation(() => takeoverConversation(conv.id), () => toast.success("Has tomado el control", { description: "El bot no responderá en esta conversación." }));
  const send = useConvMutation((t) => sendManualMessage(conv.id, t), (d) => {
    setText("");
    if (d.delivered === false) toast.warning("Mensaje guardado, pero no entregado", { description: "Revisa la conexión del canal en «Conectar cuenta»." });
  });

  if (!conv.manual_mode) {
    return (
      <div className="border-t border-[#EDE7DE] bg-[#FAF8F5] px-4 py-3 flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-[#8A7E70]">El bot responde automáticamente en esta conversación.</span>
        <button
          data-testid="take-control-btn"
          onClick={() => takeover.mutate()}
          disabled={takeover.isPending}
          className="inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] hover:bg-black text-white px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {takeover.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Hand className="h-4 w-4" />} Tomar control
        </button>
      </div>
    );
  }

  const submit = () => { const t = text.trim(); if (t && !send.isPending) send.mutate(t); };
  return (
    <div className="border-t border-amber-200 bg-white flex items-center gap-2 px-3 py-2.5">
      <input
        data-testid="manual-message-input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        placeholder="Escribe tu respuesta al paciente…"
        maxLength={2000}
        className="flex-1 bg-white border border-[#E7E2DA] rounded-full px-4 py-2 text-sm outline-none focus:border-amber-400"
      />
      <button
        data-testid="manual-send-btn"
        onClick={submit}
        disabled={send.isPending || !text.trim()}
        className="h-10 w-10 rounded-full flex items-center justify-center text-white shrink-0 disabled:opacity-50"
        style={{ background: accent }}
        title="Enviar"
      >
        {send.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4.5 w-4.5" />}
      </button>
    </div>
  );
};
